/* ==========================================
   HEADER SCROLL — StickyHeader Custom Element
   ========================================== */

if (!customElements.get('sticky-header')) {
  class StickyHeader extends HTMLElement {
    constructor() {
      super();
      this.header = this.querySelector('.pompeii-header');
      this.wrapper = this;
      this.threshold = parseInt(this.dataset.scrollThreshold, 10) || 100;
      this.stickyType = this.dataset.stickyType || 'on-scroll-up';
      this.lastScrollY = 0;
      this.ticking = false;
      this.scrolled = false;
      this.hidden = false;
    }

    connectedCallback() {
      if (!CSS.supports('backdrop-filter', 'blur(75px)') &&
          !CSS.supports('-webkit-backdrop-filter', 'blur(75px)')) {
        document.documentElement.classList.add('no-backdrop-filter');
      }

      this.onScroll = this._onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this._update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    _onScroll() {
      if (!this.ticking) {
        requestAnimationFrame(() => {
          this._update();
          this.ticking = false;
        });
        this.ticking = true;
      }
    }

    _update() {
      const currentY = window.scrollY;
      const wasScrolled = this.scrolled;

      this.scrolled = currentY > this.threshold;
      if (this.scrolled !== wasScrolled) {
        this.classList.toggle('header-wrapper--scrolled', this.scrolled);
        this.dataset.scrolled = this.scrolled ? 'true' : 'false';
      }

      /* Only hide/show for on-scroll-up; 'always' never hides */
      if (this.stickyType === 'on-scroll-up') {
        if (this.scrolled) {
          const delta = currentY - this.lastScrollY;
          if (delta > 5 && !this.hidden) {
            this.classList.add('header-wrapper--hidden');
            this.hidden = true;
          } else if (delta < -5 && this.hidden) {
            this.classList.remove('header-wrapper--hidden');
            this.hidden = false;
          }
        } else if (this.hidden) {
          this.classList.remove('header-wrapper--hidden');
          this.hidden = false;
        }
      }

      this.lastScrollY = currentY;
    }
  }

  customElements.define('sticky-header', StickyHeader);
}

/* ==========================================
   HEADER GROUP — stack scrolling-bar above header
   ========================================== */
(function setupHeaderStack() {
  function init() {
    const groups = document.querySelectorAll('.shopify-section-group-header-group');
    let bar = null;
    let header = null;
    groups.forEach((g) => {
      if (g.querySelector('.scrolling-bar')) {
        g.classList.add('scrolling-bar-section');
        bar = g;
      } else if (g.querySelector('sticky-header, .header-wrapper')) {
        header = g;
      }
    });
    function measure() {
      const h = bar ? bar.offsetHeight : 0;
      document.documentElement.style.setProperty('--scrolling-bar-height', h + 'px');
      if (header) header.style.top = h + 'px';
    }
    measure();
    if (bar) {
      const ro = new ResizeObserver(measure);
      ro.observe(bar);
    }
    window.addEventListener('resize', measure);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ==========================================
   HEADER SEARCH — Toggle overlay
   ========================================== */

if (!customElements.get('header-search')) {
  class HeaderSearch extends HTMLElement {
    connectedCallback() {
      this.overlay = document.getElementById('SearchOverlay');
      if (!this.overlay) return;

      this.input = this.overlay.querySelector('.predictive-search__input, .header__search-input');

      this.querySelectorAll('[data-search-toggle]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) this.close();
      });

      this.overlay.querySelectorAll('[data-search-close]').forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.overlay.classList.contains('is-active')) {
          this.close();
        }
      });
    }

    open() {
      this.overlay.classList.add('is-active');
      document.body.classList.add('search-overlay-open');
      document.body.style.overflow = 'hidden';
      if (this.input) {
        setTimeout(() => this.input.focus(), 200);
      }
    }

    close() {
      this.overlay.classList.remove('is-active');
      document.body.classList.remove('search-overlay-open');
      document.body.style.overflow = '';
    }
  }

  customElements.define('header-search', HeaderSearch);
}

/* ==========================================
   CART COUNTER — Fetch /cart.js
   ========================================== */

/* ==========================================
   MEGAMENU — Hover open/close
   ========================================== */
(function initMegaMenu() {
  function setup() {
    const wrapper = document.querySelector('[data-nav-style="megamenu"]');
    if (!wrapper) return;

    const triggers = wrapper.querySelectorAll('[data-mega-trigger]');
    const panels   = wrapper.querySelectorAll('[data-mega-panel]');
    if (!triggers.length || !panels.length) return;

    const closeDelay = 350;
    let closeTimer = null;

    function cancelClose() {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
    }

    function openPanel(handle) {
      cancelClose();
      panels.forEach(p => {
        p.classList.toggle('is-open', p.dataset.megaPanel === handle);
      });
    }

    function scheduleClose() {
      cancelClose();
      closeTimer = setTimeout(() => {
        panels.forEach(p => p.classList.remove('is-open'));
        closeTimer = null;
      }, closeDelay);
    }

    function scheduleCloseWhenOutside(event) {
      if (event.relatedTarget && wrapper.contains(event.relatedTarget)) return;
      scheduleClose();
    }

    triggers.forEach(trigger => {
      trigger.addEventListener('mouseenter', () => openPanel(trigger.dataset.megaTrigger));
      trigger.addEventListener('mouseleave', scheduleCloseWhenOutside);
      trigger.addEventListener('focus', () => openPanel(trigger.dataset.megaTrigger));
    });

    wrapper.addEventListener('mouseenter', cancelClose);
    wrapper.addEventListener('mouseleave', scheduleClose);

    panels.forEach(panel => {
      panel.addEventListener('mouseenter', cancelClose);
      panel.addEventListener('mouseleave', scheduleCloseWhenOutside);
    });

    /* Close on Escape or click outside */
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') panels.forEach(p => p.classList.remove('is-open'));
    });
    document.addEventListener('click', e => {
      if (!wrapper.contains(e.target)) panels.forEach(p => p.classList.remove('is-open'));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();

/* ==========================================
   MENU HIGHLIGHTS — color selected menu items
   Reads JSON from <script data-menu-highlights> and applies inline color
   to any menu link whose label matches (case-insensitive).
   ========================================== */
(function setupMenuHighlights() {
  function getLinkLabel(el) {
    var spans = el.querySelectorAll(':scope > span');
    for (var i = 0; i < spans.length; i++) {
      if (!spans[i].classList.contains('mobile-menu__chevron')) {
        return (spans[i].textContent || '').trim();
      }
    }
    return (el.textContent || '').trim();
  }

  function apply() {
    var dataEl = document.querySelector('script[data-menu-highlights]');
    if (!dataEl) return;
    var highlights;
    try { highlights = JSON.parse(dataEl.textContent.trim() || '[]'); } catch (e) { return; }
    if (!highlights || !highlights.length) return;

    var map = {};
    highlights.forEach(function (h) {
      if (h && h.text && h.color) map[h.text.toLowerCase().trim()] = h.color;
    });
    if (!Object.keys(map).length) return;

    var selectors = [
      '.pompeii-header__nav-link',
      '.pompeii-header__dropdown-link',
      '.pompeii-mega__link',
      '.pompeii-mega__column-title',
      '.pompeii-mega__column-link',
      '.mobile-menu__link',
      '.mobile-menu__submenu-link'
    ];
    document.querySelectorAll(selectors.join(',')).forEach(function (el) {
      var label = getLinkLabel(el).toLowerCase();
      if (map[label]) {
        el.style.setProperty('color', map[label], 'important');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }
  document.addEventListener('mobile-menu:opened', apply);
})();

if (!customElements.get('cart-counter')) {
  class CartCounter extends HTMLElement {
    connectedCallback() {
      this.countEl = this.querySelector('[data-cart-count]');
      if (!this.countEl) return;
      this.refresh();
      document.addEventListener('cart:updated', () => this.refresh());
    }

    async refresh() {
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!res.ok) return;
        const data = await res.json();
        this.countEl.textContent = data.item_count > 0 ? `(${data.item_count})` : '';
      } catch (e) {
        // silently fail
      }
    }
  }

  customElements.define('cart-counter', CartCounter);
}
